import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  TrendingUp,
  Download,
  Printer,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  RefreshCw,
  Building2,
  Users,
  BarChart3
} from "lucide-react";

const SalesProfitReportPage = () => {
  usePageTitle("Sales & Profit Report");
  const { token } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'total_sales', direction: 'desc' });

  // Get date range from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const dateFrom = urlParams.get('from');
  const dateTo = urlParams.get('to');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/sales-profit${params.toString() ? `?${params}` : ''}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error("Failed to fetch report");
      }
    } catch (error) {
      toast.error("Error loading report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [token, dateFrom, dateTo]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-indigo-600" />;
  };

  const filteredAndSortedPartners = useMemo(() => {
    if (!reportData?.partners) return [];
    
    let filtered = reportData.partners;
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.client_code?.toLowerCase().includes(term) ||
        p.client_name?.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;
      
      if (sortConfig.key === 'client_code' || sortConfig.key === 'client_name') {
        return sortConfig.direction === 'asc' 
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  }, [reportData, searchTerm, sortConfig]);

  const exportToCSV = () => {
    if (!reportData?.partners) return;
    
    const headers = ["Partner Code", "Partner Name", "Total Sales", "Medicine Cost", "Shipping Cost", "Total GP", "Self Share", "Partner Share", "GP %", "Parcels"];
    const rows = filteredAndSortedPartners.map(p => [
      p.client_code,
      p.client_name,
      p.total_sales.toFixed(2),
      p.medicine_cost.toFixed(2),
      p.shipping_cost.toFixed(2),
      p.total_gp.toFixed(2),
      p.self_share.toFixed(2),
      p.partner_share.toFixed(2),
      p.gp_margin + "%",
      p.parcel_count
    ]);
    
    // Add grand total
    rows.push([
      "GRAND TOTAL",
      "",
      reportData.grand_total.total_sales.toFixed(2),
      reportData.grand_total.medicine_cost.toFixed(2),
      reportData.grand_total.shipping_cost.toFixed(2),
      reportData.grand_total.total_gp.toFixed(2),
      reportData.grand_total.self_share.toFixed(2),
      reportData.grand_total.partner_share.toFixed(2),
      reportData.grand_total.gp_margin + "%",
      reportData.grand_total.parcel_count
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales_profit_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported!");
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 print:p-2">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          table { font-size: 9px; }
          th, td { padding: 3px 5px !important; }
        }
      `}</style>

      {/* Header - Non-printable controls */}
      <div className="no-print sticky top-0 z-20 bg-white dark:bg-gray-800 border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-500/10">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Sales & Profit Report</h1>
                <p className="text-sm text-muted-foreground">
                  Generated: {reportData?.generated_at ? format(new Date(reportData.generated_at), 'dd MMM yyyy, HH:mm') : '-'}
                  {(dateFrom || dateTo) && (
                    <span className="ml-2">
                      | Period: {dateFrom || 'Start'} to {dateTo || 'Today'}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button size="sm" onClick={handlePrint} className="bg-indigo-600 hover:bg-indigo-700">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-[1600px] mx-auto p-6 print:p-2 print:max-w-none">
        {/* Print Header */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold text-center">Sales & Profit Report</h1>
          <p className="text-center text-gray-600">
            Generated: {reportData?.generated_at ? format(new Date(reportData.generated_at), 'dd MMM yyyy, HH:mm') : '-'}
            {(dateFrom || dateTo) && ` | Period: ${dateFrom || 'Start'} to ${dateTo || 'Today'}`}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/30 dark:to-blue-950/30 border-indigo-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-indigo-600 uppercase">Total Sales</p>
              <p className="text-xl font-bold text-indigo-700 mt-1">
                ₹{(reportData?.grand_total?.total_sales || 0).toLocaleString()}
              </p>
              <p className="text-xs text-indigo-500">{reportData?.grand_total?.parcel_count || 0} parcels</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-green-600 uppercase">Total GP</p>
              <p className="text-xl font-bold text-green-700 mt-1">
                ₹{(reportData?.grand_total?.total_gp || 0).toLocaleString()}
              </p>
              <p className="text-xs text-green-500">{reportData?.grand_total?.gp_margin || 0}% margin</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border-blue-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-blue-600 uppercase">Self Share</p>
              <p className="text-xl font-bold text-blue-700 mt-1">
                ₹{(reportData?.grand_total?.self_share || 0).toLocaleString()}
              </p>
              <p className="text-xs text-blue-500">Company profit</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30 border-purple-200">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-purple-600 uppercase">Partner Share</p>
              <p className="text-xl font-bold text-purple-700 mt-1">
                ₹{(reportData?.grand_total?.partner_share || 0).toLocaleString()}
              </p>
              <p className="text-xs text-purple-500">Partner profit</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase">Partners</p>
              <p className="text-xl font-bold mt-1">{reportData?.partner_count || 0}</p>
              <p className="text-xs text-muted-foreground">Active partners</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="no-print mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by partner code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Main Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-indigo-50 dark:bg-indigo-950/30">
                    <TableHead className="w-12 font-semibold">#</TableHead>
                    <TableHead 
                      className="font-semibold cursor-pointer hover:bg-indigo-100"
                      onClick={() => handleSort('client_code')}
                    >
                      <div className="flex items-center">
                        Partner Code
                        <SortIcon columnKey="client_code" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold cursor-pointer hover:bg-indigo-100"
                      onClick={() => handleSort('client_name')}
                    >
                      <div className="flex items-center">
                        Partner Name
                        <SortIcon columnKey="client_name" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-right cursor-pointer hover:bg-indigo-100"
                      onClick={() => handleSort('total_sales')}
                    >
                      <div className="flex items-center justify-end">
                        Total Sales
                        <SortIcon columnKey="total_sales" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-right cursor-pointer hover:bg-indigo-100 text-green-600"
                      onClick={() => handleSort('total_gp')}
                    >
                      <div className="flex items-center justify-end">
                        Total GP
                        <SortIcon columnKey="total_gp" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-right cursor-pointer hover:bg-indigo-100 text-blue-600"
                      onClick={() => handleSort('self_share')}
                    >
                      <div className="flex items-center justify-end">
                        Self Share
                        <SortIcon columnKey="self_share" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-right cursor-pointer hover:bg-indigo-100 text-purple-600"
                      onClick={() => handleSort('partner_share')}
                    >
                      <div className="flex items-center justify-end">
                        Partner Share
                        <SortIcon columnKey="partner_share" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-right cursor-pointer hover:bg-indigo-100"
                      onClick={() => handleSort('parcel_count')}
                    >
                      <div className="flex items-center justify-end">
                        Parcels
                        <SortIcon columnKey="parcel_count" />
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-center">GP %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedPartners.map((partner, idx) => (
                    <TableRow 
                      key={partner.client_code} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="font-mono font-medium">{partner.client_code}</TableCell>
                      <TableCell>{partner.client_name}</TableCell>
                      <TableCell className="text-right font-medium">
                        ₹{partner.total_sales.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-green-600 font-medium">
                        ₹{partner.total_gp.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-blue-600">
                        ₹{partner.self_share.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-purple-600">
                        ₹{partner.partner_share.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">{partner.parcel_count}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-xs">
                          {partner.gp_margin}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Grand Total Row */}
                  <TableRow className="bg-indigo-100 dark:bg-indigo-900/50 font-bold border-t-2">
                    <TableCell colSpan={3} className="text-right">
                      GRAND TOTAL ({filteredAndSortedPartners.length} partners)
                    </TableCell>
                    <TableCell className="text-right">
                      ₹{(reportData?.grand_total?.total_sales || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      ₹{(reportData?.grand_total?.total_gp || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-blue-600">
                      ₹{(reportData?.grand_total?.self_share || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-purple-600">
                      ₹{(reportData?.grand_total?.partner_share || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {reportData?.grand_total?.parcel_count || 0}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="text-xs bg-indigo-600">
                        {reportData?.grand_total?.gp_margin || 0}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Print Footer */}
        <div className="hidden print:block mt-4 pt-4 border-t text-center text-xs text-gray-500">
          <p>EAC - Sales & Profit Report | Page 1</p>
        </div>
      </div>
    </div>
  );
};

export default SalesProfitReportPage;
